import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError, errorMessage } from '../../lib/api';
import { authApi } from '../../lib/endpoints';
import { AuthLayout } from './AuthLayout';
import { safeNext } from './AuthGate';
import { completeLogin } from './session';
import { useAuthConfig } from './useAuthConfig';

export function DiscordButton({ label }: { label: string }) {
  return (
    <a className="btn btn-block" href="/api/auth/discord?mode=login" data-testid="discord-login">
      {label}
    </a>
  );
}

export function LoginScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const config = useAuthConfig();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      completeLogin(await authApi.login({ login: login.trim(), password }));
      void navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_CREDENTIALS') setError('Benutzername/E-Mail oder Passwort ist falsch.');
      else if (err instanceof ApiError && err.code === 'ACCOUNT_DISABLED') setError(`Dieses Konto ist gesperrt. ${err.message}`);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Willkommen zurück!" subtitle="Schön, dass du wieder am Feuer sitzt." testId="login-screen">
      <form onSubmit={submit} noValidate>
        <div className="field">
          <label htmlFor="login-name">Benutzername oder E-Mail</label>
          <input id="login-name" className="input" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} required autoFocus data-testid="login-username" />
        </div>
        <div className="field">
          <label htmlFor="login-password">Passwort</label>
          <input
            id="login-password"
            type="password"
            className="input"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password"
          />
        </div>
        {error && (
          <p className="error-text" role="alert" data-testid="login-error">
            {error}
          </p>
        )}
        <button className="btn btn-primary btn-block" disabled={busy || !login || !password} data-testid="login-submit">
          {busy ? 'Anmelden …' : 'Anmelden'}
        </button>
        {config.discordLogin && (
          <>
            <div className="auth-divider">oder</div>
            <DiscordButton label="Mit Discord anmelden" />
          </>
        )}
      </form>
      <p className="auth-footer">
        {config.registrationOpen ? (
          <>
            Noch kein Konto?{' '}
            <Link to={`/register${params.get('next') ? `?next=${encodeURIComponent(next)}` : ''}`} data-testid="goto-register">
              Registrieren
            </Link>
          </>
        ) : (
          'Registrierung ist derzeit geschlossen – frag die Admins nach einem Konto.'
        )}
      </p>
    </AuthLayout>
  );
}
